import type { CoverageValue } from '../components/Coverage/Coverage';
import type {
	FertilizationBase,
	MaintenanceMixture,
	MaintenanceMixtureProduct,
	MaintenanceReferenceValue,
} from '../types';
import { toCoverage } from './coverage';
import { getMaintenanceReferenceId, normalizeMaintenanceMonth } from './maintenanceAdviceMapper';

export interface MaintenanceExecutionProduct {
	_id: string;
	name?: string;
	commercialName?: string;
}

export interface ExecutionSource {
	fertilizations?: FertilizationBase[];
	mixtures?: MaintenanceMixture[];
	sectors?: MaintenanceReferenceValue[];
}

export interface ExecutionProductLine {
	id: string;
	name: string;
	planned: number | null;
	applied: number | null;
	coverage: CoverageValue | null;
}

export interface ExecutionApplicationGroup {
	date: string;
	applicationNumber: number;
	planned: number;
	applied: number;
	lines: ExecutionProductLine[];
}

export interface ExecutionPeriodTable {
	groups: ExecutionApplicationGroup[];
	totals: {
		planned: number;
		applied: number;
		coverage: CoverageValue | null;
	};
}

type ProductAccumulator = {
	name: string;
	plannedTotal: number;
	hasPlanned: boolean;
	applied: number | null;
	isMass: boolean;
};

const UNIT_TO_BASE: Record<MaintenanceMixtureProduct['unit'], number> = {
	kg: 1,
	g: 0.001,
	L: 1,
	mL: 0.001,
};

const isoDate = (value: string) => value.slice(0, 10);

const formatDayMonth = (value: string) => {
	const [, month, day] = isoDate(value).split('-');
	return month && day ? `${day}/${month}` : value;
};

export const currentYearMonth = (now = new Date()) =>
	`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;

export const isUpcomingPeriod = (period: string, now = new Date()) => {
	const normalized = normalizeMaintenanceMonth(period);
	return !!normalized && normalized > currentYearMonth(now);
};

const sectorSizes = (sectors: MaintenanceReferenceValue[] = []) =>
	new Map(
		sectors
			.filter(
				(sector): sector is Exclude<MaintenanceReferenceValue, string> => typeof sector === 'object'
			)
			.map((sector) => [getMaintenanceReferenceId(sector), Number(sector.size) || 0])
	);

const productName = (id: string, products: MaintenanceExecutionProduct[], fallback?: string) => {
	const product = products.find((item) => item._id === id);
	return fallback || product?.name || product?.commercialName || 'Produto não encontrado';
};

export const mapMaintenanceExecutionPeriod = (
	{ fertilizations = [], mixtures = [], sectors = [] }: ExecutionSource,
	products: MaintenanceExecutionProduct[] = [],
	month: string
): ExecutionPeriodTable => {
	const monthKey = normalizeMaintenanceMonth(month);
	const emptyTable: ExecutionPeriodTable = {
		groups: [],
		totals: { planned: 0, applied: 0, coverage: null },
	};
	if (!monthKey) return emptyTable;

	const sizes = sectorSizes(sectors);
	const byDate = new Map<string, Map<string, ProductAccumulator>>();
	const sectorsByDate = new Map<string, Set<string>>();

	const accumulator = (date: string, id: string, name: string, isMass: boolean) => {
		const lines = byDate.get(date) ?? new Map<string, ProductAccumulator>();
		byDate.set(date, lines);
		const line = lines.get(id) ?? {
			name,
			plannedTotal: 0,
			hasPlanned: false,
			applied: null,
			isMass,
		};
		lines.set(id, line);
		return line;
	};

	mixtures
		.filter((mixture) => isoDate(mixture.date).slice(0, 7) === monthKey)
		.forEach((mixture) => {
			const date = isoDate(mixture.date);
			sectorsByDate.set(date, (sectorsByDate.get(date) ?? new Set()).add(mixture.sectorId));
			mixture.products.forEach((product) => {
				const line = accumulator(
					date,
					product.id,
					productName(product.id, products, product.name),
					product.unit === 'kg' || product.unit === 'g'
				);
				line.plannedTotal += product.dose * UNIT_TO_BASE[product.unit];
				line.hasPlanned = true;
			});
		});

	const mixtureById = new Map(mixtures.map((mixture) => [mixture._id, mixture]));
	const applicationDates = (fertilization: FertilizationBase) => {
		const linked = fertilization.mixtureId ? mixtureById.get(fertilization.mixtureId) : undefined;
		return linked ? [linked.date] : fertilization.dates;
	};

	fertilizations.forEach((fertilization) => {
		applicationDates(fertilization)
			.filter((date): date is string => !!date && isoDate(date).slice(0, 7) === monthKey)
			.forEach((rawDate) => {
				fertilization.fertilizers.forEach(({ id, dose, name }) => {
					if (!id || dose == null || !Number.isFinite(dose)) return;
					const line = accumulator(isoDate(rawDate), id, productName(id, products, name), true);
					line.applied = (line.applied ?? 0) + dose;
				});
			});
	});

	const groups: ExecutionApplicationGroup[] = [...byDate.entries()]
		.sort(([a], [b]) => a.localeCompare(b))
		.map(([date, linesById], index) => {
			const area = [...(sectorsByDate.get(date) ?? [])].reduce(
				(total, sectorId) => total + (sizes.get(sectorId) ?? 0),
				0
			);
			const entries = [...linesById.entries()].map(([id, line]) => ({
				id,
				line,
				planned: line.hasPlanned && area > 0 ? line.plannedTotal / area : null,
			}));
			const massEntries = entries.filter(({ line }) => line.isMass);
			return {
				date: formatDayMonth(date),
				applicationNumber: index + 1,
				planned: massEntries.reduce((total, entry) => total + (entry.planned ?? 0), 0),
				applied: massEntries.reduce((total, entry) => total + (entry.line.applied ?? 0), 0),
				lines: entries.map(({ id, line, planned }) => ({
					id,
					name: line.name,
					planned,
					applied: line.applied,
					coverage: toCoverage(line.applied ?? 0, planned),
				})),
			};
		});

	const planned = groups.reduce((total, group) => total + group.planned, 0);
	const applied = groups.reduce((total, group) => total + group.applied, 0);

	return {
		groups,
		totals: { planned, applied, coverage: toCoverage(applied, planned) },
	};
};
